import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Select, Space, Tag, Modal, Form, Dropdown } from 'antd';
import { PlusOutlined, SearchOutlined, MoreOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader } from '../../components/common/PageHeader';
import { useAdminUsersList, useInviteAdmin, useSuspendAdmin, useActivateAdmin, useRemoveAdmin } from '../../hooks/useAdminUsers';
import { ADMIN_ROLE_LABELS } from '../../lib/constants';
import type { AdminAdminUserListItem } from '../../types/adminUsers';
import dayjs from 'dayjs';

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'red',
  finance_admin: 'blue',
  support_agent: 'green',
};

export function AdminUsersListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useState({ page: 1, per_page: 20, search: '', role: undefined as string | undefined });
  const { data, isLoading } = useAdminUsersList(params);
  const invite = useInviteAdmin();
  const suspend = useSuspendAdmin();
  const activate = useActivateAdmin();
  const remove = useRemoveAdmin();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form] = Form.useForm();

  const handleInvite = () => {
    form.validateFields().then((values) => {
      invite.mutate(values, {
        onSuccess: () => { setInviteOpen(false); form.resetFields(); },
      });
    });
  };

  const columns: ColumnsType<AdminAdminUserListItem> = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (name: string, r) => (
        <a onClick={() => navigate(`/system/admin-users/${r.id}`)}>
          <div style={{ fontWeight: 500 }}>{name || r.email}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{r.email}</div>
        </a>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'admin_role',
      key: 'admin_role',
      render: (role: string) => <Tag color={ROLE_COLORS[role]}>{ADMIN_ROLE_LABELS[role] || role}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Suspended'}</Tag>,
    },
    {
      title: '2FA',
      dataIndex: 'admin_2fa_enabled',
      key: '2fa',
      render: (enabled: boolean) => <Tag color={enabled ? 'green' : 'orange'}>{enabled ? 'Enabled' : 'Not Set'}</Tag>,
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login_at',
      key: 'last_login',
      render: (d: string | null) => d ? dayjs(d).format('MMM D, YYYY HH:mm') : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              { key: 'view', label: 'View Details', onClick: () => navigate(`/system/admin-users/${record.id}`) },
              { type: 'divider' },
              record.is_active
                ? { key: 'suspend', label: 'Suspend', danger: true, onClick: () => suspend.mutate({ id: record.id, data: { reason: 'Suspended by admin', revoke_sessions: true } }) }
                : { key: 'activate', label: 'Activate', onClick: () => activate.mutate(record.id) },
              { key: 'remove', label: 'Remove Admin', danger: true, onClick: () => remove.mutate(record.id) },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Admin Users"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setInviteOpen(true)}>
            Invite Admin
          </Button>
        }
      />
      <Space style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by name or email..."
          value={params.search}
          onChange={(e) => setParams((s) => ({ ...s, search: e.target.value, page: 1 }))}
          style={{ width: 300 }}
          allowClear
        />
        <Select
          placeholder="Filter by role"
          style={{ width: 180 }}
          allowClear
          value={params.role}
          onChange={(v) => setParams((s) => ({ ...s, role: v, page: 1 }))}
          options={[
            { label: 'Super Admin', value: 'super_admin' },
            { label: 'Finance Admin', value: 'finance_admin' },
            { label: 'Support Agent', value: 'support_agent' },
          ]}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={data?.data}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: params.page,
          total: data?.total,
          pageSize: params.per_page,
          onChange: (p, ps) => setParams((s) => ({ ...s, page: p, per_page: ps || 20 })),
          showSizeChanger: true,
          showTotal: (t) => `${t} admin users`,
        }}
      />
      <Modal
        title="Invite Admin User"
        open={inviteOpen}
        onOk={handleInvite}
        onCancel={() => { setInviteOpen(false); form.resetFields(); }}
        confirmLoading={invite.isPending}
        okText="Send Invitation"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]}>
            <Input placeholder="admin@example.com" />
          </Form.Item>
          <Form.Item name="full_name" label="Full Name" rules={[{ required: true, message: 'Name required' }]}>
            <Input placeholder="John Doe" />
          </Form.Item>
          <Form.Item name="admin_role" label="Role" rules={[{ required: true, message: 'Role required' }]}>
            <Select
              placeholder="Select role"
              options={[
                { label: 'Super Admin — Full system control', value: 'super_admin' },
                { label: 'Finance Admin — Billing & subscriptions', value: 'finance_admin' },
                { label: 'Support Agent — Customer support', value: 'support_agent' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
